import React, { useId, useState } from 'react'
import { ClickAwayListener, Popper } from '@mui/material'
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react'
import Input from '../../ui/Input'
import type { AppendixCodeItemOption } from '../../../types/codeIndex'
import { codeItemChoice, formatCodeItemTarget } from '../../../utils/appendixTable'

type Props = {
  items: AppendixCodeItemOption[]
  selected: AppendixCodeItemOption | null
  disabled?: boolean
  invalid?: boolean
  ariaLabel: string
  onSelect: (choice: string) => void
}

/** Both levels stay in the same picker; entering a position list does not change the draft. */
export default function CodeItemReferencePicker({ items, selected, disabled, invalid, ariaLabel, onSelect }: Props) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const [parent, setParent] = useState<AppendixCodeItemOption | null>(null)
  const [search, setSearch] = useState('')
  const listboxId = useId()
  const close = () => { setAnchor(null); setParent(null); setSearch('') }
  const options = (parent?.positions || items).filter(item =>
    formatCodeItemTarget(item).toLowerCase().includes(search.trim().toLowerCase()))

  return (
    <ClickAwayListener onClickAway={close}>
      <div className="min-w-0">
        <button
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-expanded={Boolean(anchor)}
          aria-invalid={invalid || undefined}
          title={selected && !selected.positions?.length ? formatCodeItemTarget(selected) : undefined}
          disabled={disabled}
          onClick={event => anchor ? close() : setAnchor(event.currentTarget)}
          className={`flex w-full items-center justify-between gap-2 rounded border bg-white px-2 py-1.5 text-left text-sm text-gray-700 disabled:cursor-not-allowed disabled:bg-[#F8FAFC] ${invalid ? 'border-[#B3261E]' : 'border-[#CBD5E1]'}`}
        >
          <span className="truncate">{selected && !selected.positions?.length ? formatCodeItemTarget(selected) : 'Select'}</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden="true" />
        </button>
        <Popper open={Boolean(anchor)} anchorEl={anchor} placement="bottom-start" sx={{ zIndex: 1600, width: 300 }}>
          <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg" onKeyDown={event => { if (event.key === 'Escape') { close(); anchor?.focus() } }}>
            {parent ? (
              <button type="button" onClick={() => { setParent(null); setSearch('') }} className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm text-[#1D5176]">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to code items
              </button>
            ) : null}
            <div className="p-2"><Input value={search} onChange={event => setSearch(event.target.value)} aria-label={parent ? 'Search positions' : 'Search code items'} placeholder="Search" autoFocus /></div>
            <div id={listboxId} role="listbox" aria-label={parent ? `Positions of ${parent.code}` : 'Code items'} className="max-h-60 overflow-y-auto">
              {options.length === 0 ? <p className="px-3 py-2 text-sm text-gray-500">No matches</p> : options.map(item => (
                <button
                  key={codeItemChoice(item)} type="button" role="option"
                  aria-selected={selected === item}
                  title={formatCodeItemTarget(item)}
                  onClick={() => {
                    if (item.positions?.length) { setParent(item); setSearch(''); return }
                    onSelect(codeItemChoice(item)); close()
                  }}
                  className="flex w-full items-center justify-between gap-2 border-t border-gray-100 px-3 py-2 text-left text-sm hover:bg-[#EEF6FF]"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-gray-500">Code item {item.code}</span>
                    <span className="block truncate">{formatCodeItemTarget(item, false)}</span>
                  </span>
                  {item.positions?.length ? <ChevronRight className="h-4 w-4 flex-shrink-0" aria-label="Has positions" /> : null}
                </button>
              ))}
            </div>
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  )
}
